<template>
  <b-container class="chat-box">
    <b-modal
      ref="login-cta"
      centered
      hide-footer
      hide-header
      content-class="login-cta-modal"
    >
      <div class="text-center p-3">
        <h4 class="font-weight-bold mb-3">
          ¡Inicia sesión para hacer tu propia planificación personalizada!
        </h4>
        <p class="text-muted">
          Esta es una planificación de ejemplo. Con tu cuenta puedes elegir tus
          propios cursos, objetivos e indicadores, y personalizar la clase como
          quieras.
        </p>
        <p v-if="bonusHtml" class="mb-3" v-html="bonusHtml"></p>
        <b-button pill variant="primary" size="lg" @click="login">
          Iniciar sesión
        </b-button>
      </div>
    </b-modal>
    <b-row>
      <b-col cols="12" lg="10" class="mx-auto">
        <div
          v-for="(msg, msgidx) in messages"
          :key="`${demoRunId}-${msgidx}`"
          class="chat-record mt-2"
        >
          <ChatMessageComponent
            :style="{
              '--main-color': mainColor,
              '--secondary-color': secondaryColor,
            }"
            :initMsg="msg"
            :key="`${demoRunId}-${msgidx}`"
            :msgidx="msgidx"
            :ref="`msgRef-${msg.name}`"
            :context="context"
            :condition="condition"
            @select-card="handleCardClick"
            @continue="continueChat"
            @scroll-to-bottom="scrollToBottom"
            @scroll-to-msg="scrollToMsg"
            @update-condition="updateCondition"
            @go-to-step="goToStep"
          />
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>
<script>
// Logged-out demo for the Planifications product. Reached when a visitor
// clicks the planification teaser (see Default.changeView). It replays the
// REAL product flow — same ChatMessageComponent, same question graph shape as
// ChatbotPlanification, and the real result components (PlanificationTable →
// DailyPlanificationModified + SlidesOnlineComponent) — but every selection is
// scripted and the "generated" content comes pre-recorded from
// planificationDemoFixture.js, so no AI/backend endpoint is ever hit (demo
// visitors may be bots). The demo advances on its own; any click on the
// scripted steps opens a "log in" CTA modal instead of mutating state.
//
// Demo mode travels inside the chat context (context.demo = fixture), which is
// how PlanificationTable and its children detect it without touching
// ChatMessageComponent (shared by every other chatbot).
import ChatMessageComponent from "./ChatMessageComponent.vue";
import PlanificationTable from "./PlanificationTable";
import fixture from "./planificationDemoFixture";
import informationService from "@/services/informationService";
import {
  sectionStep as pasoSeccion,
  sectionComponent as seccion,
} from "./serviceSections";
import PlanificationVivo from "./PlanificationVivo.vue";
import { fetchRewards, applyBonus, bonusLine, loginLabel } from "./signupRewards";
import { scrollToElementTop, scrollToPageBottom } from "@/utils/scrollToTop";

// El teal de Planificaciones (`accent_key='secondary'` en pages.py) y su
// versión oscura para texto: sobre blanco el teal claro no llega a 4,5:1.
const ACENTO = "#00adb5";
const ACENTO_INK = "#227580";

// Los pasos de sección de esta demo vuelven a `menu` y son navegación real
// (`interactive`, ver handleCardClick), no pasos del guion.
const sectionStep = (name, text, extra) =>
  pasoSeccion(name, text, {
    interactive: true,
    nextQuestionIndex: "menu",
    ...extra,
  });

const sectionComponent = (content, block) =>
  seccion(content, block, ACENTO, ACENTO_INK);

export default {
  name: "PlanificationDemo",
  components: { ChatMessageComponent },
  // Absorb the props the layout's router-view binds so they don't leak onto the
  // root element; none of them are needed by the demo.
  props: {
    bannerLoaded: { type: Boolean, required: false, default: false },
    routeName: { type: String, required: false, default: "" },
    tags: { type: Array, required: false, default: () => [] },
  },
  data() {
    return {
      mainColor: "#20b2aa",
      secondaryColor: "#20b2aa",
      starIcon: require("@/assets/img/icono-estrella.svg"),
      currentQuestionIndex: "",
      condition: "",
      context: {},
      messages: [],
      demoTimers: [],
      // Bumped on every replay so the message v-for keys change and Vue tears
      // down and re-mounts the chat components (index-only keys would reuse the
      // old instances, and their mounted() autoplay chain would never re-fire —
      // which made "Ver el demo de nuevo" look broken).
      demoRunId: 0,
      // Premios de una cuenta nueva (usos gratis + misiones); llegan del
      // backend en created() y se cosen en los menús.
      rewards: null,
      // El contenido de /planificaciones-de-clase/, que alimenta los
      // pasos de información, preguntas frecuentes y precios.
      serviceContent: null,
      questions: this.buildQuestions(),
    };
  },
  created() {
    // Los usos gratis salen de la config y la información del servicio, de la
    // página de producto. Si alguna falla, esa mitad simplemente no se cuenta.
    Promise.all([
      fetchRewards("Planificaciones"),
      informationService.getServiceContent("planificaciones-de-clase").catch(
        () => null
      ),
    ]).then(([rewards, content]) => {
      this.rewards = rewards;
      this.serviceContent = content;
      this.applyContent();
      this.applyRewards();
      this.startDemo();
    });
  },
  computed: {
    token() {
      return this.$store.state.login.token;
    },
    currentQuestion() {
      return this.questions[this.currentQuestionIndex];
    },
    bonusHtml() {
      return bonusLine(this.rewards);
    },
  },
  watch: {
    // Once the visitor logs in (from a CTA), send them to the real product.
    token(val) {
      if (val) this.goToRealProduct();
    },
  },
  mounted() {
    // A logged-in user has no business on the demo (e.g. landed here after a
    // login that reloaded the page) — forward them to the real chatbot.
    if (this.token) this.goToRealProduct();
  },
  beforeDestroy() {
    this.clearDemoTimers();
  },
  methods: {
    // The question graph mirrors ChatbotPlanification's daily flow with static
    // answers (no services, no prices). `demoScript` marks the steps the demo
    // plays by itself: which answer indexes to pick and how long to "think".
    buildQuestions() {
      const cursos = {
        "Pre-kinder": "PK",
        Kinder: "K",
        "1° básico": "1°",
        "2° básico": "2°",
        "3° básico": "3°",
        "4° básico": "4°",
        "5° básico": "5°",
        "6° básico": "6°",
        "7° básico": "7°",
        "8° básico": "8°",
        "1° medio": "1°M",
        "2° medio": "2°M",
        "3° medio": "3°M",
        "4° medio": "4°M",
      };
      // Always include the captured subject so its card exists to auto-pick,
      // regardless of what selection the fixture was generated with.
      const asignaturas = Array.from(
        new Set([
          fixture.asignatura.name,
          "Lenguaje y comunicación / Lengua y Literatura",
          "Matemáticas",
          "Ciencias naturales",
          "Historia, geografía y ciencias sociales",
          "Música",
        ])
      );
      return {
        // Landing menu: visitors arrive here straight from an ad, so the demo
        // no longer autoplays on load — they pick what they want to see. Mirrors
        // EurekaDemo's welcome menu; `interactive: true` tells handleCardClick
        // these clicks are real navigation, not scripted-demo taps.
        welcomeMenu: {
          name: "welcomeMenu",
          text: "<strong>¿Qué te gustaría hacer?</strong>",
          type: "options",
          interactive: true,
          answersLoaded: false,
          single_choice: true,
          answers: [],
          process_dic: [
            {
              name: "Iniciar sesión para probar la versión real",
              cardClass: "highlighted-option pulse-option",
              option: "login",
            },
            {
              name: "Ver cómo se crea una planificación",
              nextQuestionIndex: "vivo",
            },
            // Apagado por ahora: la simulación de arriba cuenta lo mismo y se
            // ve mejor. El recorrido guionizado sigue entero —sus pasos, su
            // fixture y sus descargas reales— esperando esta línea.
            // {
            //   name: "Probar el demo paso a paso y descargar los archivos",
            //   nextQuestionIndex: "welcomeMessage",
            // },
            {
              name: "Conocer más detalles del servicio",
              nextQuestionIndex: "details",
            },
            {
              name: "Ver las preguntas frecuentes",
              nextQuestionIndex: "faq",
            },
            {
              name: "Ver los precios y planes",
              nextQuestionIndex: "prices",
            },
            {
              name: "Recibir una cotización",
              nextQuestionIndex: "bill",
              always: true,
            },
          ],
          // Mismo armado que `menu`: una opción ya tomada tiene su nombre como
          // clave en `context` y se cae del menú en vez de volver.
          getAnswers(context) {
            this.answers = this.process_dic
              .filter(
                (ans) =>
                  ans.always ||
                  !ans.nextQuestionIndex ||
                  !(ans.nextQuestionIndex in context)
              )
              .map((ans) => ({
                ...ans,
                isSelected: false,
                class: "col-md-7 col-12 my-2",
                bootstrap_classes: "text-left px-2 font-weight-bold",
              }));
            return Promise.resolve(this.answers);
          },
          routine() {},
        },
        // Lo mismo que cuenta la página de producto —los tres principios del
        // DUA y los dos archivos que te llevas— con su mismo texto. Antes eran
        // seis tarjetas escritas a mano acá dentro.
        details: sectionStep(
          "details",
          `👋 ¡Hola! Con Utopie creas planificaciones de clase con IA en segundos: eliges curso, asignatura, Objetivo de Aprendizaje e indicadores y obtienes la planificación completa con sus diapositivas, lista para descargar.`,
          // Es la primera pantalla de la demo, así que el menú aparece solo
          // debajo: no hay nada que responder acá. Y como al llegar queda su
          // nombre en `context`, los dos menús dejan de ofrecerla.
          // El botón de Google va bajo el saludo, para entrar sin bajar
          // hasta el menú.
          {
            dontShowNextButton: true,
            nextQuestionIndex: "welcomeMenu",
            loginButton: true,
          }
        ),
        // La simulación de /planificaciones-de-clase/: se ve la clase
        // y el PowerPoint escribiéndose solos, con una clase real de 3° básico.
        vivo: {
          name: "vivo",
          text: "Mira cómo se crea una planificación completa, con su PowerPoint, en menos de un minuto:",
          type: "custom-component",
          component: PlanificationVivo,
          interactive: true,
          single_choice: false,
          answersLoaded: true,
          answers: [],
          dontShowNextButton: true,
          nextQuestionIndex: "menu",
          routine() {},
        },
        faq: sectionStep(
          "faq",
          "Estas son las preguntas que más nos hacen sobre las planificaciones:",
          { dontShowNextButton: true }
        ),
        prices: sectionStep(
          "prices",
          "Las planificaciones se pueden usar de tres formas, según lo que necesites:",
          { dontShowNextButton: true }
        ),
        // Same cotización endpoint as the logged-in chatbot (service "PLANI").
        // Anonymous callers get a Turnstile token from the api interceptor.
        bill: {
          name: "bill",
          text: "¡Excelente! Indícanos tu correo y la cantidad de planificaciones a cotizar y te enviaremos la cotización.",
          type: "inputs",
          answersLoaded: true,
          answers: [
            {
              name: "email",
              type: "email",
              placeholder: "Escribe tu correo aquí",
              class: "col-12 col-md-6 mb-2",
            },
            {
              name: "applications",
              type: "number",
              placeholder: "Cantidad de planificaciones",
              class: "col-12 col-md-6 mb-2",
            },
          ],
          nextMessage: "Obtener cotización!",
          extraFunction: (data) => {
            informationService
              .sendEurekaBill({
                email: data.email,
                applications: data.applications,
                service: "PLANI",
              })
              .then((resp) => {
                this.$toasted.success(resp.message);
              })
              .catch(() => {
                this.$toasted.error(
                  "No pudimos enviar tu cotización. Por favor, inténtalo nuevamente."
                );
              });
          },
          nextQuestionIndex: "menu",
          routine: () => {},
        },
        // Menu shown again after details/cotización. Rebuilt on every visit so
        // an option already taken (its step name is a key in `context`) drops
        // out instead of coming back.
        menu: {
          name: "menu",
          text: "¿Qué te gustaría hacer ahora?",
          type: "options",
          interactive: true,
          answersLoaded: false,
          single_choice: true,
          answers: [],
          process_dic: [
            {
              name: "Iniciar sesión para probar la versión real",
              cardClass: "highlighted-option pulse-option",
              option: "login",
            },
            {
              name: "Ver cómo se crea una planificación",
              nextQuestionIndex: "vivo",
            },
            // Apagado por ahora: la simulación de arriba cuenta lo mismo y se
            // ve mejor. El recorrido guionizado sigue entero —sus pasos, su
            // fixture y sus descargas reales— esperando esta línea.
            // {
            //   name: "Probar el demo paso a paso y descargar los archivos",
            //   nextQuestionIndex: "welcomeMessage",
            // },
            {
              name: "Conocer más detalles del servicio",
              nextQuestionIndex: "details",
            },
            {
              name: "Ver las preguntas frecuentes",
              nextQuestionIndex: "faq",
            },
            {
              name: "Ver los precios y planes",
              nextQuestionIndex: "prices",
            },
            {
              name: "Recibir una cotización",
              nextQuestionIndex: "bill",
              // La cotización no se agota: quien ya pidió una puede querer
              // otra por otra cantidad.
              always: true,
            },
          ],
          getAnswers(context) {
            this.answers = this.process_dic
              .filter(
                (ans) =>
                  ans.always ||
                  !ans.nextQuestionIndex ||
                  !(ans.nextQuestionIndex in context)
              )
              .map((ans) => ({
                ...ans,
                isSelected: false,
                class: "col-md-7 col-12 my-2",
                bootstrap_classes: "text-left px-2 font-weight-bold",
              }));
            return Promise.resolve(this.answers);
          },
          routine() {},
        },
        welcomeMessage: {
          name: "welcomeMessage",
          text: `👋 ¡Hola! Te mostraré en vivo cómo se crea una planificación de clase con IA en Utopie.<br/>
            Mira cómo elegimos <strong>curso</strong>, <strong>asignatura</strong>, <strong>Objetivo de Aprendizaje</strong> e <strong>indicadores</strong>, personalizamos la clase, y la planificación completa con sus diapositivas se genera en segundos.`,
          type: "informative",
          answersLoaded: true,
          answers: [],
          nextQuestionIndex: "curso",
          getAnswers() {
            return Promise.resolve(this.answers);
          },
          routine() {},
        },
        curso: {
          name: "curso",
          text: "Hagamos una planificación de clase. \n Dime ¿Con qué curso vamos a comenzar?",
          hint: "Escoge solo un curso",
          type: "options",
          answersLoaded: true,
          single_choice: true,
          nextQuestionIndex: "asignatura",
          answers: Object.entries(cursos).map(([original_name, name]) => ({
            original_name,
            name,
            isSelected: false,
            cols: "col-sm-2 col-3",
            bootstrap_classes: "font-weight-bold",
            nextQuestionIndex: "asignatura",
          })),
          demoScript: {
            picks: [
              Object.keys(cursos).indexOf(fixture.curso.original_name),
            ],
            delay: 1600,
          },
          // Static answers still need getAnswers: ChatMessageComponent only
          // copies initMsg.answers into its local state on the getAnswers
          // branch when the message has a hint.
          getAnswers() {
            return Promise.resolve(this.answers);
          },
          routine() {},
        },
        asignatura: {
          name: "asignatura",
          text: "¡Muy bien! Ahora dime ¿Con qué asignatura?",
          hint: "Escoge solo una asignatura",
          type: "options",
          answersLoaded: true,
          single_choice: true,
          nextQuestionIndex: "single_oa",
          answers: asignaturas.map((name) => ({
            name,
            isSelected: false,
            cols: "col-6",
            nextQuestionIndex: "single_oa",
          })),
          demoScript: {
            picks: [asignaturas.indexOf(fixture.asignatura.name)],
            delay: 1400,
          },
          getAnswers() {
            return Promise.resolve(this.answers);
          },
          routine() {},
        },
        single_oa: {
          name: "single_oa",
          text: (context) => {
            const curso = context.curso.original_name;
            const asignatura = context.asignatura.name;
            return `Estos son los objetivos de aprendizaje de ${curso} para  ${asignatura}<br> ¿Cuáles te gustaría trabajar?`;
          },
          hint: () => {
            return `Escoge tu objetivo de aprendizaje. <br> Las <img src='${this.starIcon}' width='22px' class='pl-1 mb-2'/></img> indican los OAs prioritarios`;
          },
          type: "options",
          answersLoaded: true,
          single_choice: true,
          nextQuestionIndex: "single_indicadores",
          answers: fixture.oaOptions.map((oa) => ({
            name:
              "<strong style='font-size: 1.2rem;'>" +
              oa.short +
              "</strong> - " +
              oa.summary,
            description: oa.description,
            isSelected: false,
            cols: "col-sm-12 col-12",
            subtags: oa.subtags,
            bootstrap_classes: "text-left px-2",
            priority: oa.priority,
            nextQuestionIndex: "single_indicadores",
          })),
          demoScript: {
            picks: [fixture.oaSelectedIndex],
            delay: 2200,
          },
          getAnswers() {
            return Promise.resolve(this.answers);
          },
          routine() {},
        },
        single_indicadores: {
          name: "single_indicadores",
          text: "Ahora, selecciona los indicadores para tu planificación.",
          type: "options",
          single_choice: false,
          answersLoaded: false,
          max: 3,
          dontShowNextButton: true,
          nextQuestionIndex: "special_conditions",
          answers: [],
          getAnswers(context) {
            if (this.answers.length === 0 && context.single_oa.subtags) {
              context.single_oa.subtags.forEach((subtag, index) => {
                this.answers.push({
                  name:
                    "<strong style='font-size: 1.2rem;'>" +
                    `Ind. ${index + 1}: ` +
                    "</strong> " +
                    subtag.summary,
                  description: subtag.name,
                  isSelected: false,
                  cols: "col-sm-12 col-12",
                  bootstrap_classes: "text-left px-2",
                });
              });
            }
            this.answersLoaded = true;
            return Promise.resolve(this.answers);
          },
          demoScript: {
            picks: fixture.indicadorSelectedIndexes,
            delay: 1800,
          },
          routine() {},
        },
        special_conditions: {
          name: "special_conditions",
          text: "¿Hay algo más que te gustaría agregar o personalizar para tu clase? Puedes incluir detalles sobre la temática, requisitos especiales o cualquier otro aspecto que desees.",
          type: "options",
          answersLoaded: true,
          single_choice: true,
          nextQuestionIndex: "special_conditions_text",
          answers: [
            {
              name: "Sí",
              isSelected: false,
              nextQuestionIndex: "special_conditions_text",
            },
            {
              name: "No",
              isSelected: false,
              nextQuestionIndex: "daily_planification",
            },
          ],
          demoScript: { picks: [0], delay: 1400 },
          getAnswers() {
            return Promise.resolve(this.answers);
          },
          routine() {},
        },
        special_conditions_text: {
          name: "special_conditions_text",
          text: "Escribe aquí lo que deseas agregar",
          type: "input-text",
          answersLoaded: true,
          dontShowNextButton: true,
          answers: [],
          nextQuestionIndex: "daily_planification",
          // Consumed by SpecialConditions.vue: types the fixture text by
          // itself and routes any user interaction to the login CTA.
          demoTypeText: fixture.specialConditionsText,
          demoInteract: () => this.showLoginCta(),
          routine() {},
        },
        daily_planification: {
          name: "daily_planification",
          text: "Muy bien. Esta es la planificación que has creado",
          type: "custom-component",
          answersLoaded: true,
          answers: [],
          dontShowNextButton: true,
          component: PlanificationTable,
          // Having getAnswers keeps ChatMessageComponent from auto-emitting
          // `continue` for this step (same trick as the real chatbot); the demo
          // advances via go-to-step once the slides finish.
          getAnswers() {
            return Promise.resolve(this.answers);
          },
          nextQuestionIndex: "closingMessage",
          routine() {},
        },
        closingMessage: {
          name: "closingMessage",
          text: "Y así de fácil: planificación completa, diapositivas y descargas en un par de minutos. ¿Quieres crear la tuya con tus propios cursos?",
          type: "options",
          interactive: true,
          answersLoaded: true,
          single_choice: true,
          answers: [
            {
              name: "Iniciar sesión y crear mi propia planificación",
              isSelected: false,
              class: "col-md-7 col-12 my-2",
              bootstrap_classes: "text-left px-2 font-weight-bold",
              cardClass: "highlighted-option pulse-option",
              option: "login",
            },
            {
              name: "Ver el demo de nuevo",
              isSelected: false,
              class: "col-md-7 col-12 my-2",
              bootstrap_classes: "text-left px-2 font-weight-bold",
              option: "replay",
            },
            {
              name: "Recibir una cotización",
              isSelected: false,
              class: "col-md-7 col-12 my-2",
              bootstrap_classes: "text-left px-2 font-weight-bold",
              nextQuestionIndex: "bill",
            },
          ],
          getAnswers() {
            return Promise.resolve(this.answers);
          },
          routine() {},
        },
      };
    },
    // Reparte el contenido de la página de producto por los pasos que lo
    // dibujan. Sin contenido —el endpoint no respondió— esos tres pasos no
    // tienen nada que mostrar y salen de los menús: mejor no ofrecerlos que
    // abrir una sección vacía. El demo interactivo y la cotización siguen.
    applyContent() {
      const secciones = { details: "info", faq: "faq", prices: "planes" };
      if (this.serviceContent) {
        Object.entries(secciones).forEach(([paso, block]) => {
          this.questions[paso].component = sectionComponent(
            this.serviceContent,
            block
          );
        });
        return;
      }
      ["welcomeMenu", "menu"].forEach((name) => {
        const paso = this.questions[name];
        const sirve = (ans) => !(ans.nextQuestionIndex in secciones);
        paso.answers = (paso.answers || []).filter(sirve);
        paso.process_dic = (paso.process_dic || []).filter(sirve);
      });
    },
    // Los menús prometen los usos gratis con el número real de la config.
    applyRewards() {
      // Acá y no en created: resetDemo rehace el grafo y vuelve a pasar por acá.
      this.questions.details.loginLabel = loginLabel(this.rewards, "Iniciar sesión para probar la versión real");
      applyBonus(this.questions, this.rewards, [
        "welcomeMenu",
        "menu",
        "closingMessage",
      ]);
    },
    // `startStep` is the service description on a fresh visit —quien llega de
    // la publicidad quiere saber qué es esto antes que un menú— y el primer
    // paso del guion en una repetición (ya pidió ver la demo).
    startDemo(startStep = "details") {
      this.$store.commit("chatbot/clear");
      // service/type/period mimic what the real chatbot puts in its context so
      // PlanificationTable's computeds resolve; `demo` carries the fixture and
      // switches every downstream component into replay mode.
      this.context = {
        service: "Planification",
        type: { option: "accurate" },
        period: { option: "daily" },
        demo: fixture,
      };
      this.condition = "";
      this.messages = [];
      this.currentQuestionIndex = startStep;
      const question = this.questions[this.currentQuestionIndex];
      this.context[question.name] = {};
      this.messages.push(question);
    },
    resetDemo() {
      this.clearDemoTimers();
      this.demoRunId += 1;
      this.questions = this.buildQuestions();
      this.applyContent();
      this.applyRewards();
      this.startDemo("welcomeMessage");
      window.scrollTo(0, 0);
    },
    addDemoTimer(fn, delay) {
      this.demoTimers.push(setTimeout(fn, delay));
    },
    clearDemoTimers() {
      this.demoTimers.forEach((timer) => clearTimeout(timer));
      this.demoTimers = [];
    },
    // Every click on an answer card lands here. Real CTAs (login/replay) work;
    // anything else is a scripted step, so we pitch the login modal instead of
    // letting the visitor change the selection.
    handleCardClick(answer, msgidx, ansidx) {
      if (answer && answer.option === "login") {
        this.login();
        return;
      }
      if (answer && answer.option === "replay") {
        this.resetDemo();
        return;
      }
      // Menu steps (welcomeMenu / menu / closingMessage) are real navigation:
      // select the card and route on it, like the logged-in chatbot does.
      if (this.currentQuestion && this.currentQuestion.interactive) {
        if (this.messages[msgidx].name !== this.currentQuestion.name) return;
        this.autoSelect(msgidx, ansidx);
        this.continueChat();
        return;
      }
      this.showLoginCta();
    },
    showLoginCta() {
      this.$refs["login-cta"].show();
    },
    // Autoplay driver: ChatMessageComponent emits scroll-to-msg right after a
    // message finished typing and its answers became visible — the natural
    // moment to start "picking" answers like a user would.
    runDemoScript(question) {
      const script = question.demoScript;
      if (!script || script.played) return;
      script.played = true;
      const msgidx = this.messages.length - 1;
      const stepDelay = 900;
      script.picks.forEach((ansidx, k) => {
        this.addDemoTimer(() => {
          this.autoSelect(msgidx, ansidx);
        }, script.delay + k * stepDelay);
      });
      this.addDemoTimer(() => {
        this.continueChat();
      }, script.delay + (script.picks.length - 1) * stepDelay + 1100);
    },
    autoSelect(msgidx, ansidx) {
      const message = this.messages[msgidx];
      if (!message || !message.answers[ansidx]) return;
      if (message.single_choice) {
        message.answers.forEach((ans) => {
          ans.isSelected = false;
        });
      }
      message.answers[ansidx].isSelected = true;
    },
    continueChat() {
      const question = this.currentQuestion;
      if (!question || question.isFinal) return;
      if (question.type === "options") {
        this.context[question.name] = question.single_choice
          ? question.answers.find((ans) => ans.isSelected) || {}
          : question.answers.filter((ans) => ans.isSelected);
      }
      this.messages.slice(-1)[0].answered = true;
      const selected = this.context[question.name];
      this.currentQuestionIndex =
        question.single_choice && selected && selected.nextQuestionIndex
          ? selected.nextQuestionIndex
          : question.nextQuestionIndex;
      this.loadQuestion(this.currentQuestionIndex);
    },
    loadQuestion(questionIndex) {
      try {
        const question = this.questions[questionIndex];
        this.context[question.name] = {};
        this.messages.push(question);
      } catch (error) {
        console.log(error);
      }
    },
    // Emitted by PlanificationTable (relayed by ChatMessageComponent) when the
    // last slide finishes: jump the chat to the closing CTA.
    goToStep(step) {
      // Los CTA de los planes piden sesión, no un paso del grafo.
      if (step === "login") {
        this.login();
        return;
      }
      if (!this.questions[step]) return;
      this.messages.slice(-1)[0].answered = true;
      this.currentQuestionIndex = step;
      this.loadQuestion(step);
    },
    // Fired when SpecialConditions finished auto-typing the personalization
    // text; a short pause and the demo moves on to the generation step.
    updateCondition(condition) {
      this.condition = condition;
      if (
        this.currentQuestion &&
        this.currentQuestion.name === "special_conditions_text"
      ) {
        this.addDemoTimer(() => this.continueChat(), 1500);
      }
    },
    scrollToBottom() {
      scrollToPageBottom();
    },
    scrollToMsg(name) {
      const msgRef = this.$refs[`msgRef-${name}`];
      // Un paso que se auto-encadena —la descripción del servicio con la que
      // abre la demo— añade el menú sin que nadie lo pida. Hacerle caso al
      // scroll dejaría al visitante debajo de lo que acaba de llegar a leer.
      const idx = this.messages.map((m) => m.name).lastIndexOf(name);
      const previo = idx > 0 ? this.messages[idx - 1] : null;
      if (msgRef && msgRef.length && !(previo && previo.dontShowNextButton)) {
        const el = msgRef.at(-1).$el;
        const parentMsg = el && el.parentNode;
        // Misma correccion que en ChatbotPlanification: `block: "center"`
        // centraba el mensaje que trae la tabla completa, y como mide varias
        // pantallas el usuario quedaba en su mitad, pasada la tabla. Esta es la
        // ruta sin login, o sea la primera que puede ver alguien que llega de
        // la publicidad.
        scrollToElementTop(parentMsg);
      }
      if (this.currentQuestion && this.currentQuestion.name === name) {
        this.runDemoScript(this.currentQuestion);
      }
    },
    login() {
      this.$refs["login-cta"].hide();
      this.$emit("open-login");
    },
    goToRealProduct() {
      if (this.$router.currentRoute.name !== "planification") {
        this.$router.push("planification").catch(() => {});
      }
    },
  },
};
</script>
<style scoped>
.chat-box {
  margin: 10px auto;
  width: 100%;
  align-items: space-between;
  justify-content: space-between;
}
</style>
